import React, { useContext, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
const BASE_URL = process.env.REACT_APP_API_URL;

const AuthContext = React.createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const history = useHistory();

  // 🔹 Restore user from localStorage on first load
  useEffect(() => {
    const token = localStorage.getItem("token");
    const userData = localStorage.getItem("user");
    
    if (token && userData) {
      try {
        const user = JSON.parse(userData);
        setCurrentUser(user);
      } catch (error) {
        console.error("Error parsing user data:", error);
        // Clean up corrupted data
        localStorage.removeItem("token");
        localStorage.removeItem("user");
      }
    }
    setLoading(false);
  }, []);

  // 🔹 Login: call API, store token + user
  const login = async (email, password) => {
    try {
      const res = await fetch(`${BASE_URL}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (!res.ok || data.error) {
        throw new Error(data.message || "Login failed");
      }

      // Save token + user
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.data));
      // console.log("Login successful, user:", data.data.id);
      
      setCurrentUser(data.data);

      return true;
    } catch (err) {
      console.error("Login error:", err.message);
      return false;
    }
  };

  // 🔹 Logout: clear everything and redirect
  const logout = () => {
    // Clear localStorage
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    
    // Clear state
    setCurrentUser(null);
    
    // Redirect to login
    history.push("/admin/login");
  };

  // 🔹 Refresh current user from API and update local state + storage
  const refreshCurrentUser = async () => {
    try {
      const userData = localStorage.getItem('user');
      if (!userData) return null;
      const parsed = JSON.parse(userData);
      if (!parsed || !parsed.id) return null;
      const res = await fetch(`${BASE_URL}/users/${parsed.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const json = await res.json();
      if (!res.ok || json.error) return null;
      localStorage.setItem('user', JSON.stringify(json.data));
      setCurrentUser(json.data);
      return json.data;
    } catch (err) {
      // ignore
      return null;
    }
  };

  // 🔹 Check if user is authenticated
  const isAuthenticated = () => {
    const token = localStorage.getItem("token");
    return !!(token && currentUser);
  };

  const value = {
    currentUser,
    login,
    logout,
    isAuthenticated: isAuthenticated(),
    refreshCurrentUser,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};